/**
 * Utility functions for HubSpot tracking and Google Click ID (gclid) management
 */

/**
 * Captures gclid and LinkedIn click ID from URL and stores them in sessionStorage
 * Should be called on page load
 * 
 * IMPORTANT: This stores the IDs in sessionStorage, so even if the URL loses them
 * during navigation, they will still be available when submitting the form.
 */
export function captureGclid() {
    if (typeof window === 'undefined') return;

    try {
        const urlParams = new URLSearchParams(window.location.search);
        const gclid = urlParams.get('gclid');
        const liFatId = urlParams.get('li_fat_id'); // LinkedIn click ID
        
        if (gclid) {
            // Store in sessionStorage (persists for the session)
            sessionStorage.setItem('gclid', gclid);
            // Also store timestamp to track when it was captured
            sessionStorage.setItem('gclid_timestamp', new Date().toISOString());
        }

        if (liFatId) {
            // Store LinkedIn click ID
            sessionStorage.setItem('li_fat_id', liFatId);
            sessionStorage.setItem('li_fat_id_timestamp', new Date().toISOString());
        }
    } catch (error) {
        // Silently handle errors
    }
}

/**
 * Preserves tracking parameters (gclid, li_fat_id) when navigating to a new URL
 * This function prioritizes stored values from sessionStorage to ensure we don't lose
 * tracking IDs even if the current URL doesn't have them.
 * 
 * @param {string} basePath - The base path to navigate to (e.g., "/register-company")
 * @returns {string} The path with tracking parameters appended
 */
export function preserveTrackingParams(basePath) {
    if (typeof window === 'undefined') return basePath;

    try {
        // First, check sessionStorage (most reliable - persists even if URL loses params)
        const storedGclid = sessionStorage.getItem('gclid');
        const storedLiFatId = sessionStorage.getItem('li_fat_id');
        
        // Also check current URL params (in case they're still there)
        const urlParams = new URLSearchParams(window.location.search);
        const urlGclid = urlParams.get('gclid');
        const urlLiFatId = urlParams.get('li_fat_id');
        
        const trackingParams = new URLSearchParams();
        
        // Prioritize stored values (they're more reliable), but use URL if stored is missing
        const gclid = storedGclid || urlGclid;
        const liFatId = storedLiFatId || urlLiFatId;
        
        if (gclid) {
            trackingParams.set('gclid', gclid);
        }
        
        if (liFatId) {
            trackingParams.set('li_fat_id', liFatId);
        }
        
        // If we have any tracking params, append them to the path
        if (trackingParams.toString()) {
            const separator = basePath.includes('?') ? '&' : '?';
            const finalPath = `${basePath}${separator}${trackingParams.toString()}`;
            return finalPath;
        }
        
        return basePath;
    } catch (error) {
        return basePath;
    }
}

/**
 * Retrieves stored gclid from sessionStorage
 * @returns {string|null} The gclid value or null if not found
 */
export function getStoredGclid() {
    if (typeof window === 'undefined') return null;
    
    try {
        return sessionStorage.getItem('gclid');
    } catch (error) {
        return null;
    }
}

/**
 * Retrieves stored LinkedIn click ID from sessionStorage
 * @returns {string|null} The LinkedIn click ID value or null if not found
 */
export function getStoredLinkedInClickId() {
    if (typeof window === 'undefined') return null;
    
    try {
        return sessionStorage.getItem('li_fat_id');
    } catch (error) {
        return null;
    }
}

/**
 * Maps professional service flags to human-readable names for HubSpot
 */
function mapProfessionalServices(professionalServices) {
    const serviceMap = {
        'data_roadmap_flg': 'Custom Data Roadmap',
        'pipeline_setup_flg': 'Set up my data pipelines',
        'build_dashboard_report_flg': 'Build dashboards or reports',
        'automate_report_alert_flg': 'Automate reporting & alerts',
        'datawarehousing_flg': 'Design or optimize my data warehouse',
        'kpi_metric_definitions_flg': 'Define business KPIs and metrics',
        'data_quality_validation_flg': 'Improve data quality & validation checks',
        'data_governance_flg': 'Implement Data governance & access controls',
        'predictive_ai_flg': 'Enable AI or predictive analytics',
        'data_support_flg': 'Get ongoing managed data support'
    };

    if (!professionalServices || !Array.isArray(professionalServices)) {
        return [];
    }

    return professionalServices
        .map(service => serviceMap[service])
        .filter(Boolean)
        .join('; '); // HubSpot multi-select properties use semicolon separator
}

/**
 * Sends user data to HubSpot including all form fields and tracking IDs
 * This should be called when a user signs up
 * 
 * @param {Object} userData - User information to send to HubSpot
 * @param {string} userData.email - User's email address
 * @param {string} userData.firstName - User's first name
 * @param {string} userData.lastName - User's last name
 * @param {string} userData.company - User's company name
 * @param {string} userData.phone - User's phone number (optional)
 * @param {string} userData.howDidYouHear - How did you hear about us (optional)
 * @param {string} userData.howDidYouHearOther - How did you hear about us - Other text (optional)
 * @param {boolean} userData.additionalSupport - Additional Support (Xantie consent) (optional)
 * @param {Array} userData.professionalServices - Additional Support Options array (optional)
 * @param {string} userData.industryId - Company Industry ID (optional)
 * @param {string} userData.industryOther - Company Industry - Other text (optional)
 * @param {string} userData.organizationId - Organization ID (optional)
 * @param {string} userData.organizationUserId - Organization User ID (optional)
 * @param {string} userData.gclid - Google Click ID (optional, will be retrieved from storage if not provided)
 * @param {string} userData.linkedInClickId - LinkedIn Click ID (optional, will be retrieved from storage if not provided)
 * @param {string} userData.isMainContact - Main Contact (Yes/No) (optional)
 */
export function identifyUserToHubSpot(userData) {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        // Get tracking IDs from userData or from storage
        const gclid = userData.gclid || getStoredGclid();
        const linkedInClickId = userData.linkedInClickId || getStoredLinkedInClickId();
        
        // Prepare the base identify payload
        const hubspotData = {
            email: userData.email,
            firstName: userData.firstName,
            lastName: userData.lastName,
            company: userData.company,
        };

        // Add phone if provided
        if (userData.phone) {
            hubspotData.phone = userData.phone;
        }

        // NOTE: Property names below match the internal property names from HubSpot
        // These are the exact API internal IDs from your HubSpot account

        // Add "How did you hear about us?" (Radio Select)
        if (userData.howDidYouHear) {
            hubspotData.how_did_you_hear_about_us_ = userData.howDidYouHear;
        }

        // Add "How did you hear about us? / Other" (Single line Text)
        if (userData.howDidYouHearOther) {
            hubspotData.how_did_you_hear_about_us____other = userData.howDidYouHearOther;
        }

        // Add "Additional Support" (Radio Select: Yes/No)
        if (userData.additionalSupport !== undefined) {
            hubspotData.additional_support = userData.additionalSupport 
                ? 'Yes — connect me with Xantie for additional support' 
                : 'No, I prefer to handle this myself.';
        }

        // Add "Additional Support Options" (Multiple Checkboxes)
        if (userData.professionalServices && userData.professionalServices.length > 0) {
            // HubSpot multi-select properties use semicolon-separated values
            hubspotData.additional_support_options = mapProfessionalServices(userData.professionalServices);
        }

        // Add "Company Industry" (Dropdown Select)
        if (userData.industryName) {
            hubspotData.company_industry_contact = userData.industryName;
        }

        // Add "Company Industry, Other" (Single line Text)
        if (userData.industryOther) {
            hubspotData.company_industry_contact_other = userData.industryOther;
        }

        // Add "Organization ID" (Company property - may need separate handling)
        if (userData.organizationId) {
            // Note: This might be a company property, not a contact property
            hubspotData.organization_id = userData.organizationId;
        }

        // Add "Organization User ID" (Single line Text)
        if (userData.organizationUserId) {
            hubspotData.organization_user_id = userData.organizationUserId;
        }

        // Add "Google ad click id" (Single line Text)
        if (gclid) {
            hubspotData.hs_google_click_id = gclid;
        }

        // Add "LinkedIn ad click id" (Single line Text)
        if (linkedInClickId) {
            hubspotData.linkedin_ad_click_id = linkedInClickId;
        }

        // Add "Main Contact" (Dropdown Select: Yes/No)
        if (userData.isMainContact !== undefined) {
            hubspotData.main_contact = userData.isMainContact ? 'Yes' : 'No';
        }

        // Method 1: Use HubSpot tracking queue (_hsq) - identify user with ALL data
        // This creates/updates the contact in HubSpot with all properties
        const submitToHubSpotQueue = () => {
            // Identify the user with ALL their data - this creates/updates the contact
            // The identify method accepts all custom properties
            window._hsq.push(['identify', hubspotData]);
            
            // Also track a custom event for the signup
            window._hsq.push(['trackCustomBehavioralEvent', {
                name: 'user_signup',
                properties: {
                    email: hubspotData.email,
                    company: hubspotData.company,
                    gclid: gclid || null,
                    linkedin_click_id: linkedInClickId || null
                }
            }]);
        };

        if (window._hsq) {
            submitToHubSpotQueue();
        } else {
            // Wait for HubSpot to load, then retry
            waitForHubSpot(() => {
                submitToHubSpotQueue();
            });
        }

        // Method 2: Use HubSpot REST API to create/update contact
        // This is the most reliable way to ensure the contact is created
        // Note: This requires a HubSpot form GUID. You'll need to create a form in HubSpot
        // and replace 'YOUR_FORM_GUID' with the actual form GUID
        submitToHubSpotAPI(hubspotData, gclid, linkedInClickId).catch(() => {
            // Silently handle errors
        });

        // Method 3: Try HubSpot Conversations API (for chat widget identification)
        if (window.hsConversationsAPI) {
            window.hsConversationsAPI('identify', {
                email: hubspotData.email,
                firstName: hubspotData.firstName,
                lastName: hubspotData.lastName
            });
        }
    } catch (error) {
        // Silently handle errors
    }
}

/**
 * Waits for HubSpot tracking script to load
 * @param {Function} callback - Function to call when HubSpot is ready
 * @param {number} maxAttempts - Maximum number of attempts to wait
 * @param {number} attempt - Current attempt number
 */
function waitForHubSpot(callback, maxAttempts = 10, attempt = 0) {
    if (window._hsq) {
        callback();
        return;
    }
    
    if (attempt >= maxAttempts) {
        return;
    }
    
    setTimeout(() => {
        waitForHubSpot(callback, maxAttempts, attempt + 1);
    }, 500);
}

/**
 * Submits contact data to HubSpot using REST API
 * This is the most reliable method for creating/updating contacts
 * 
 * IMPORTANT: You need to create a form in HubSpot and get its GUID.
 * To get the form GUID:
 * 1. Go to HubSpot > Marketing > Forms
 * 2. Create or edit a form
 * 3. Click "Actions" > "Share" > "Embed" 
 * 4. The form GUID is in the embed code (looks like: guid: "abc123-def456-...")
 * 
 * Replace 'YOUR_FORM_GUID' below with your actual form GUID
 * 
 * @param {Object} hubspotData - The data object to send to HubSpot
 * @param {string} gclid - Google Click ID
 * @param {string} linkedInClickId - LinkedIn Click ID
 */
async function submitToHubSpotAPI(hubspotData, gclid, linkedInClickId) {
    if (typeof window === 'undefined') return;

    try {
        // HubSpot Portal ID from the script URL: //js-na1.hs-scripts.com/22371829.js
        const HUBSPOT_PORTAL_ID = '22371829';
        // TODO: Replace with your actual HubSpot form GUID
        // You can find this by creating a form in HubSpot and looking at the embed code
        const HUBSPOT_FORM_GUID = 'YOUR_FORM_GUID'; // Replace this!
        
        // If form GUID is not set, skip API submission
        if (HUBSPOT_FORM_GUID === 'YOUR_FORM_GUID') {
            return;
        }
        
        const HUBSPOT_API_ENDPOINT = `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_GUID}`;

        // Map our data to HubSpot form fields
        // You'll need to get the actual field names from your HubSpot form
        // These are example field names - you'll need to update them to match your HubSpot form
        const fields = [];

        // Standard contact fields
        if (hubspotData.email) {
            fields.push({ name: 'email', value: hubspotData.email });
        }
        if (hubspotData.firstName) {
            fields.push({ name: 'firstname', value: hubspotData.firstName });
        }
        if (hubspotData.lastName) {
            fields.push({ name: 'lastname', value: hubspotData.lastName });
        }
        if (hubspotData.phone) {
            fields.push({ name: 'phone', value: hubspotData.phone });
        }
        if (hubspotData.company) {
            fields.push({ name: 'company', value: hubspotData.company });
        }

        // Custom fields - using exact HubSpot API internal IDs
        if (hubspotData.how_did_you_hear_about_us_) {
            fields.push({ name: 'how_did_you_hear_about_us_', value: hubspotData.how_did_you_hear_about_us_ });
        }
        if (hubspotData.how_did_you_hear_about_us____other) {
            fields.push({ name: 'how_did_you_hear_about_us____other', value: hubspotData.how_did_you_hear_about_us____other });
        }
        if (hubspotData.additional_support) {
            fields.push({ name: 'additional_support', value: hubspotData.additional_support });
        }
        if (hubspotData.additional_support_options) {
            fields.push({ name: 'additional_support_options', value: hubspotData.additional_support_options });
        }
        if (hubspotData.company_industry_contact) {
            fields.push({ name: 'company_industry_contact', value: hubspotData.company_industry_contact });
        }
        if (hubspotData.company_industry_contact_other) {
            fields.push({ name: 'company_industry_contact_other', value: hubspotData.company_industry_contact_other });
        }
        if (hubspotData.organization_id) {
            fields.push({ name: 'organization_id', value: hubspotData.organization_id });
        }
        if (hubspotData.organization_user_id) {
            fields.push({ name: 'organization_user_id', value: hubspotData.organization_user_id });
        }
        if (gclid) {
            // Using the actual HubSpot property name
            fields.push({ name: 'hs_google_click_id', value: gclid });
        }
        if (linkedInClickId) {
            fields.push({ name: 'linkedin_ad_click_id', value: linkedInClickId });
        }
        if (hubspotData.main_contact) {
            fields.push({ name: 'main_contact', value: hubspotData.main_contact });
        }

        const payload = {
            fields: fields,
            context: {
                pageUri: window.location.href,
                pageName: document.title
            }
        };

        const response = await fetch(HUBSPOT_API_ENDPOINT, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload)
        });

        if (response.ok) {
            const result = await response.json();
            return result;
        } else {
            const errorText = await response.text();
            throw new Error(`HubSpot API error: ${response.status} - ${errorText}`);
        }
    } catch (error) {
        // Don't throw - we want to continue even if API call fails
        // The _hsq identify method might still work for tracking
    }
}

